import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { exerciseLibrary } from '../services/api';
import ExercisesPanel from '../components/ExerciseLibrary/ExercisesPanel';
import EquipmentPanel from '../components/ExerciseLibrary/EquipmentPanel';
import ValidationPanel from '../components/ExerciseLibrary/ValidationPanel';
import { errorList, useAllEquipment } from '../components/ExerciseLibrary/shared';
import '../components/ExerciseLibrary/ExerciseLibrary.css';

/*
 * Biblioteca de exercícios e equipamento — administração. Um Admin, ou quem tem um perfil com
 * "Gerir Biblioteca de Exercícios", gere aqui o que o ginásio tem: o catálogo que acompanha a
 * aplicação (editável, repondo-se quando se quiser) e o que for só deste ginásio.
 *
 * O separador «Validação» é onde se aprovam «aos magotes» os vídeos filmados no ginásio; só
 * aparece a quem pode decidir (um sócio nem sabe que a fila existe).
 */

const TABS = [['exercises', 'Exercícios'], ['equipment', 'Equipamento']];

export default function AdminExerciseLibraryPage() {
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [pending, setPending] = useState(null);   // quantos ficheiros esperam validação
  const { items: machines } = useAllEquipment(reloadKey);

  const tab = requested === 'equipment' ? 'equipment' : requested === 'validation' && meta?.canManage ? 'validation' : 'exercises';

  useEffect(() => {
    let alive = true;
    exerciseLibrary.meta()
      .then(({ data }) => { if (alive) { setMeta(data); setError(null); } })
      .catch((e) => { if (alive) setError(errorList(e, 'Não foi possível carregar a biblioteca.').join(' ')); });
    return () => { alive = false; };
  }, [reloadKey]);

  const changed = useCallback(() => setReloadKey((k) => k + 1), []);
  const selectTab = (key) => setParams(key === 'exercises' ? {} : { tab: key }, { replace: true });

  if (error) return <div className="lib-page"><div className="lib-notice bad" role="alert">{error}</div></div>;
  if (!meta) return <div className="lib-page"><div className="lib-loading" role="status">A carregar a biblioteca…</div></div>;

  const { catalog, counts } = meta;
  const tabs = meta.canManage ? [...TABS, ['validation', 'Validação']] : TABS;
  const counted = { exercises: counts.exercises, equipment: counts.equipment, validation: pending ?? '' };

  return (
    <div className="lib-page">
      <div className="lib-header">
        <div>
          <h1>Biblioteca de exercícios</h1>
          <p>Os exercícios e as máquinas do ginásio, com as suas imagens. O catálogo traz o essencial; acrescente o que for só vosso.</p>
        </div>
      </div>

      {!meta.canManage && (
        <div className="lib-notice warn" role="status">Pode consultar a biblioteca, mas não tem permissão para a alterar.</div>
      )}

      {catalog.appliedVersion == null ? (
        <div className="lib-banner warn" role="status">
          <strong>O catálogo ainda não foi aplicado a este ginásio.</strong>
          <span>Aplica-se no arranque da aplicação ({catalog.bundledExercises} exercícios, {catalog.bundledEquipment} máquinas, versão {catalog.bundledVersion}).</span>
        </div>
      ) : (
        <div className={`lib-banner${catalog.appliedVersion < catalog.bundledVersion ? ' warn' : ''}`} role="status">
          <strong>Catálogo v{catalog.appliedVersion}</strong>
          <span>{counts.exercisesActive} de {counts.exercises} exercícios ativos</span>
          <span>O ginásio tem {counts.equipmentAvailable} de {counts.equipment} máquinas</span>
          {catalog.appliedVersion < catalog.bundledVersion && <span>Há uma versão mais recente (v{catalog.bundledVersion}); aplica-se no próximo arranque.</span>}
        </div>
      )}

      <div className="lib-tabs" role="tablist" aria-label="Biblioteca">
        {tabs.map(([key, label]) => (
          <button key={key} type="button" role="tab" id={`lib-tab-${key}`} aria-controls={`lib-panel-${key}`} className="lib-tab"
            aria-selected={tab === key} onClick={() => selectTab(key)}>
            {label}{counted[key] !== '' && <span className="lib-count">{counted[key]}</span>}
          </button>
        ))}
      </div>

      {/* Os três painéis ficam montados: trocar de separador não perde os filtros nem a página. */}
      <div role="tabpanel" id="lib-panel-exercises" aria-labelledby="lib-tab-exercises" hidden={tab !== 'exercises'}>
        <ExercisesPanel meta={meta} machines={machines} onCatalogChanged={changed} />
      </div>
      <div role="tabpanel" id="lib-panel-equipment" aria-labelledby="lib-tab-equipment" hidden={tab !== 'equipment'}>
        <EquipmentPanel meta={meta} onChanged={changed} />
      </div>
      {meta.canManage && (
        <div role="tabpanel" id="lib-panel-validation" aria-labelledby="lib-tab-validation" hidden={tab !== 'validation'}>
          <ValidationPanel meta={meta} onCountChange={setPending} onDecided={changed} />
        </div>
      )}
    </div>
  );
}
